/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { StockItem } from "../Stock/types";
import { toBaseQty, unitOptions } from "../Stock/units";
import { PRICE_HOLD_PCT, type EditableReceiptLine } from "./types";

type Props = {
  line: EditableReceiptLine;
  stockItems: StockItem[];
  readOnly: boolean;
  /** Orders show the price change but don't hold prices (deliveries do). */
  noHold?: boolean;
  onChange: (line: EditableReceiptLine) => void;
  onRemove: () => void;
};

/** Cost per base unit and its change vs the item's last price, if known. */
export function linePricing(line: EditableReceiptLine, item: StockItem | undefined) {
  if (!item || !(line.quantity > 0)) return { unitCost: null, change: null };
  const base = toBaseQty(item, line.quantity, line.unit);
  if (!base) return { unitCost: null, change: null };
  const unitCost = (line.quantity * line.unit_price) / base;
  const change =
    item.last_cost && item.last_cost > 0 ? (unitCost / item.last_cost - 1) * 100 : null;
  return { unitCost, change };
}

export const isHeld = (line: EditableReceiptLine, item: StockItem | undefined) => {
  const { change } = linePricing(line, item);
  return change != null && change > PRICE_HOLD_PCT && !line.price_confirmed;
};

export const ReceiptLineRow = defineComponent<Props>({
  name: "ReceiptLineRow",
  props: ["line", "stockItems", "readOnly", "noHold", "onChange", "onRemove"],
  setup(props) {
    const { t } = useLocalization();

    const set = (patch: Partial<EditableReceiptLine>) =>
      props.onChange({ ...props.line, ...patch });

    const num = (e: Event) => Number((e.target as HTMLInputElement).value);

    return () => {
      const item = props.stockItems.find((s) => s.id === props.line.stock_item_id);
      const { unitCost, change } = linePricing(props.line, item);
      const held = !props.noHold && isHeld(props.line, item);
      const total = props.line.quantity * props.line.unit_price;

      return (
        <tr class={held ? "bg-warning/10" : ""}>
          <td>
            <select
              class="select select-bordered select-sm w-full min-w-40"
              disabled={props.readOnly}
              value={props.line.stock_item_id ?? ""}
              onChange={(e: Event) => {
                const id = Number((e.target as HTMLSelectElement).value);
                const next = props.stockItems.find((s) => s.id === id);
                // Default to the largest unit (units come sorted by size):
                // deliveries are usually counted in cases or bottles.
                const largest = next?.units[next.units.length - 1];
                set({ stock_item_id: id, unit: largest?.name ?? next?.base_unit ?? "" });
              }}
            >
              <option value="" disabled>
                {t("inventory.recipe.pick_item")}
              </option>
              {props.stockItems.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {(props.line.description || props.line.supplier_code) && (
              <div class="mt-1 max-w-56 truncate text-xs text-base-content/50" title={props.line.description}>
                {[props.line.supplier_code, props.line.description].filter(Boolean).join(" · ")}
              </div>
            )}
          </td>
          <td>
            <input
              type="number"
              min="0"
              step="any"
              class="input input-bordered input-sm w-20"
              disabled={props.readOnly}
              value={props.line.quantity || ""}
              onInput={(e: Event) => set({ quantity: num(e) })}
            />
          </td>
          <td>
            <select
              class="select select-bordered select-sm w-24"
              disabled={props.readOnly || !item}
              value={props.line.unit}
              onChange={(e: Event) => set({ unit: (e.target as HTMLSelectElement).value })}
            >
              {item && unitOptions(item).map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </td>
          <td>
            <input
              type="number"
              min="0"
              step="any"
              class="input input-bordered input-sm w-24"
              disabled={props.readOnly}
              value={props.line.unit_price || ""}
              onInput={(e: Event) => set({ unit_price: num(e) })}
            />
          </td>
          <td class="whitespace-nowrap text-right">
            <div class="text-sm font-medium">{total ? total.toFixed(2) : "—"}</div>
            {unitCost != null && item && (
              <div class="text-xs text-base-content/50">
                {`${unitCost.toFixed(unitCost < 1 ? 4 : 2)} / ${item.base_unit}`}
              </div>
            )}
          </td>
          <td class="whitespace-nowrap text-right text-xs">
            {change == null ? (
              <span class="text-base-content/40">—</span>
            ) : (
              <span class={change > PRICE_HOLD_PCT ? "font-semibold text-warning" : change < 0 ? "text-success" : ""}>
                {`${change > 0 ? "+" : ""}${change.toFixed(1)}%`}
              </span>
            )}
            {held && !props.readOnly && (
              <label class="mt-1 flex items-center justify-end gap-1">
                <input
                  type="checkbox"
                  class="checkbox checkbox-xs"
                  checked={props.line.price_confirmed}
                  onChange={(e: Event) =>
                    set({ price_confirmed: (e.target as HTMLInputElement).checked })
                  }
                />
                <span>{t("inventory.purchases.confirm_price")}</span>
              </label>
            )}
          </td>
          <td>
            {!props.readOnly && (
              <button
                class="btn btn-ghost btn-xs btn-square"
                aria-label={t("inventory.recipe.remove_line")}
                onClick={() => props.onRemove()}
              >
                <span class="material-symbols-outlined text-base">close</span>
              </button>
            )}
          </td>
        </tr>
      );
    };
  },
});
